import React, { useEffect, useRef, useState } from 'react';

// ── NODE URLS & DESCRIPTIONS ──────────────────────────────────────────
const NODE_META = {
  'ARHAT\nSTACK': { url: 'https://arhatinfo.com',    desc: 'Our full engineering stack'         },
  'Python':       { url: 'https://python.org',        desc: 'Core backend language'              },
  'Django':       { url: 'https://djangoproject.com', desc: 'Full-featured web framework'       },
  'FastAPI':      { url: 'https://fastapi.tiangolo.com', desc: 'High-performance async APIs'   },
  'OpenAI':       { url: 'https://openai.com',        desc: 'LLM & AI integration'              },
  'AWS':          { url: 'https://aws.amazon.com',    desc: 'Cloud infrastructure & deployment' },
  'Docker':       { url: 'https://docker.com',        desc: 'Containerised deployments'         },
  'PostgreSQL':   { url: 'https://postgresql.org',    desc: 'Primary relational database'       },
  'Airflow':      { url: 'https://airflow.apache.org',desc: 'Data pipeline orchestration'       },
  'Redis':        { url: 'https://redis.io',          desc: 'Caching & async task queues'       },
};

export default function TechConstellation() {
  const canvasRef     = useRef(null);
  const wrapRef       = useRef(null);
  const stateRef      = useRef({
    t: 0, rot1: 0, rot2: 0,
    paused: false,
    hoveredNode: null,
    pulseTimer: 0,
    pulseRings: [],
    packets: [],
  });
  const [tooltip, setTooltip] = useState(null);
  // tooltip = { label, desc, url, x, y } | null

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });

    const W = 460, H = 460;
    canvas.width  = W;
    canvas.height = H;

    const CX = W / 2, CY = H / 2;

    // ── PALETTE ──────────────────────────────────────────────────────
    const BLU  = '#35BBEA';
    const BLU2 = '#3b82f6';
    const IND  = '#818cf8';
    const CYN  = '#22d3ee';
    const WHT  = '#eef2ff';

    const INNER_R = 115;
    const OUTER_R = 195;

    // ── NODES ────────────────────────────────────────────────────────
    const NODES = [
      { label: 'ARHAT\nSTACK', cx: CX, cy: CY, r: 34, main: true,  color: BLU2, pulse: 0 },
      { label: 'Python',       cx: 0,  cy: 0,  r: 18, main: false, color: BLU,  pulse: 0, ai: 0    },
      { label: 'Django',       cx: 0,  cy: 0,  r: 18, main: false, color: BLU,  pulse: 0, ai: 0.33 },
      { label: 'FastAPI',      cx: 0,  cy: 0,  r: 18, main: false, color: CYN,  pulse: 0, ai: 0.66 },
      { label: 'OpenAI',       cx: 0,  cy: 0,  r: 16, main: false, color: IND,  pulse: 0, ao: 0    },
      { label: 'AWS',          cx: 0,  cy: 0,  r: 16, main: false, color: BLU2, pulse: 0, ao: 1/6  },
      { label: 'Docker',       cx: 0,  cy: 0,  r: 16, main: false, color: BLU,  pulse: 0, ao: 2/6  },
      { label: 'PostgreSQL',   cx: 0,  cy: 0,  r: 16, main: false, color: CYN,  pulse: 0, ao: 3/6  },
      { label: 'Airflow',      cx: 0,  cy: 0,  r: 16, main: false, color: IND,  pulse: 0, ao: 4/6  },
      { label: 'Redis',        cx: 0,  cy: 0,  r: 16, main: false, color: BLU,  pulse: 0, ao: 5/6  },
    ];

    // ── EDGES ────────────────────────────────────────────────────────
    const EDGES = [
      [0,1],[0,2],[0,3],
      [1,2],[2,3],[3,1],
      [0,4],[0,5],[0,6],[0,7],[0,8],[0,9],
      [1,4],[1,5],[2,6],[2,7],[3,8],[3,9],
      [4,5],[5,6],[6,7],[7,8],[8,9],[9,4],
    ];

    // ── ENERGY PACKETS ───────────────────────────────────────────────
    const PACKET_COLORS = [BLU, CYN, IND, BLU2];
    const s = stateRef.current;

    function spawnPacket() {
      const edge = EDGES[Math.floor(Math.random() * EDGES.length)];
      const rev  = Math.random() > 0.5;
      s.packets.push({
        from:  rev ? edge[1] : edge[0],
        to:    rev ? edge[0] : edge[1],
        t:     0,
        speed: 0.005 + Math.random() * 0.007,
        color: PACKET_COLORS[Math.floor(Math.random() * 4)],
        size:  2.5 + Math.random() * 2,
      });
    }
    for (let i = 0; i < 14; i++) spawnPacket();

    // ── HELPERS ──────────────────────────────────────────────────────
    function hexA(hex, a) {
      const r = parseInt(hex.slice(1,3), 16);
      const g = parseInt(hex.slice(3,5), 16);
      const b = parseInt(hex.slice(5,7), 16);
      return `rgba(${r},${g},${b},${a})`;
    }

    function glowLine(x1, y1, x2, y2, color, alpha, width = 1) {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = color;
      ctx.lineWidth   = width;
      ctx.shadowColor = color;
      ctx.shadowBlur  = 8;
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
      ctx.restore();
    }

    function glowCircle(x, y, r, color, alpha, lw = 0.8) {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = color;
      ctx.lineWidth   = lw;
      ctx.shadowColor = color;
      ctx.shadowBlur  = 12;
      ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.stroke();
      ctx.restore();
    }

    function drawTriangle(cx, cy, r, rot, color, alpha, lw = 1.2) {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = color;
      ctx.lineWidth   = lw;
      ctx.shadowColor = color;
      ctx.shadowBlur  = 12;
      ctx.beginPath();
      for (let i = 0; i < 3; i++) {
        const a  = rot + i * Math.PI * 2 / 3;
        const px = cx + r * Math.cos(a);
        const py = cy + r * Math.sin(a);
        i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
      }
      ctx.closePath(); ctx.stroke();
      ctx.restore();
    }

    // ── HIT TEST ─────────────────────────────────────────────────────
    function getCanvasPos(e) {
      const rect  = canvas.getBoundingClientRect();
      const scaleX = W / rect.width;
      const scaleY = H / rect.height;
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top)  * scaleY,
      };
    }

    function hitNode(mx, my) {
      // test in reverse so topmost drawn node wins
      for (let i = NODES.length - 1; i >= 0; i--) {
        const n  = NODES[i];
        const dx = mx - n.cx;
        const dy = my - n.cy;
        const hr = n.r + 12; // generous hit radius
        if (dx * dx + dy * dy <= hr * hr) return i;
      }
      return -1;
    }

    // ── MOUSE EVENTS ─────────────────────────────────────────────────
    function onMouseMove(e) {
      const { x, y } = getCanvasPos(e);
      const idx = hitNode(x, y);

      if (idx >= 0) {
        s.paused      = true;
        s.hoveredNode = idx;
        canvas.style.cursor = 'pointer';

        const n    = NODES[idx];
        const meta = NODE_META[n.label] || {};
        const rect = canvas.getBoundingClientRect();
        const scaleX = rect.width  / W;
        const scaleY = rect.height / H;

        // position tooltip in page coords
        setTooltip({
          label: n.label.replace('\n', ' '),
          desc:  meta.desc || '',
          url:   meta.url  || '#',
          color: n.color,
          // canvas-relative pixels → page pixels
          x: rect.left + window.scrollX + n.cx * scaleX,
          y: rect.top  + window.scrollY + n.cy * scaleY,
        });
      } else {
        s.paused      = false;
        s.hoveredNode = null;
        canvas.style.cursor = 'default';
        setTooltip(null);
      }
    }

    function onMouseLeave() {
      s.paused      = false;
      s.hoveredNode = null;
      canvas.style.cursor = 'default';
      setTooltip(null);
    }

    // click → open URL
    function onClick(e) {
      const { x, y } = getCanvasPos(e);
      const idx = hitNode(x, y);
      if (idx < 0) return;
      const meta = NODE_META[NODES[idx].label];
      if (meta?.url) window.open(meta.url, '_blank', 'noopener,noreferrer');
    }

    canvas.addEventListener('mousemove',  onMouseMove);
    canvas.addEventListener('mouseleave', onMouseLeave);
    canvas.addEventListener('click',      onClick);

    // ── UPDATE ───────────────────────────────────────────────────────
    function update() {
      if (s.paused) return;

      NODES.forEach((n, i) => {
        if (n.main) { n.cx = CX; n.cy = CY; return; }
        if (n.ai !== undefined) {
          const a = n.ai * Math.PI * 2 - Math.PI / 2 + s.t * 0.18 + Math.sin(s.t * 0.6 + i) * 0.04;
          n.cx = CX + INNER_R * Math.cos(a);
          n.cy = CY + INNER_R * Math.sin(a);
        } else {
          const a = n.ao * Math.PI * 2 - Math.PI / 2 + s.t * (-0.10) + Math.sin(s.t * 0.4 + i) * 0.03;
          n.cx = CX + OUTER_R * Math.cos(a);
          n.cy = CY + OUTER_R * Math.sin(a);
        }
        n.pulse = (Math.sin(s.t * 1.8 + i * 1.1) + 1) / 2;
      });

      s.rot1 =  s.t * 0.45;
      s.rot2 = -s.t * 0.28;

      s.packets.forEach(p => { p.t += p.speed; });
      for (let i = s.packets.length - 1; i >= 0; i--) {
        if (s.packets[i].t >= 1) { s.packets.splice(i, 1); spawnPacket(); }
      }

      s.pulseTimer++;
      if (s.pulseTimer > 55) {
        s.pulseRings.push({ r: 0, a: 0.7, color: [BLU, CYN, IND][Math.floor(Math.random() * 3)] });
        s.pulseTimer = 0;
      }
        s.pulseRings.forEach(p => { p.r += 0.9; p.a -= 0.009; });
      for (let i = s.pulseRings.length - 1; i >= 0; i--) {
        if (s.pulseRings[i].a <= 0) s.pulseRings.splice(i, 1);
      }

      s.t += 0.014;
    }

    // ── DRAW ─────────────────────────────────────────────────────────
    function draw() {
      ctx.clearRect(0, 0, W, H);

      // subtle grid
      ctx.save();
      ctx.globalAlpha = 0.04;
      ctx.strokeStyle = BLU;
      ctx.lineWidth   = 0.5;
      for (let x = 0; x < W; x += 50) { ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,H); ctx.stroke(); }
      for (let y = 0; y < H; y += 50) { ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(W,y); ctx.stroke(); }
      ctx.restore();

      // pulse rings
      s.pulseRings.forEach(p => glowCircle(CX, CY, p.r, p.color, p.a * 0.45));

      // rotating triangles
      drawTriangle(CX, CY, 62, s.rot1,                 BLU2, 0.35, 1.2);
      drawTriangle(CX, CY, 80, s.rot2,                 CYN,  0.22, 1.0);
      drawTriangle(CX, CY, 50, s.rot1 + Math.PI / 3,  IND,  0.18, 0.8);

      // orbit rings
      ctx.save();
      ctx.globalAlpha = 0.10; ctx.strokeStyle = BLU; ctx.lineWidth = 0.8;
      ctx.setLineDash([4, 8]);
      ctx.beginPath(); ctx.arc(CX, CY, OUTER_R, 0, Math.PI * 2); ctx.stroke();
      ctx.restore();

      ctx.save();
      ctx.globalAlpha = 0.14; ctx.strokeStyle = CYN; ctx.lineWidth = 0.8;
      ctx.setLineDash([3, 6]);
      ctx.beginPath(); ctx.arc(CX, CY, INNER_R, 0, Math.PI * 2); ctx.stroke();
      ctx.setLineDash([]); ctx.restore();

      // edges
      EDGES.forEach(([a, b]) => {
        const isHovered = (s.hoveredNode === a || s.hoveredNode === b);
        glowLine(
          NODES[a].cx, NODES[a].cy,
          NODES[b].cx, NODES[b].cy,
          isHovered ? NODES[a].color : 'rgba(96,165,250,0.12)',
          isHovered ? 0.55 : 1,
          isHovered ? 1.2 : 0.6
        );
      });

      // packets (hidden when paused)
      if (!s.paused) {
        s.packets.forEach(p => {
          const na = NODES[p.from];
          const nb = NODES[p.to];
          const x  = na.cx + (nb.cx - na.cx) * p.t;
          const y  = na.cy + (nb.cy - na.cy) * p.t;
          const TRAIL = 8;
          for (let st = TRAIL; st >= 0; st--) {
            const tt = Math.max(0, p.t - st * 0.012);
            const tx = na.cx + (nb.cx - na.cx) * tt;
            const ty = na.cy + (nb.cy - na.cy) * tt;
            ctx.save();
            ctx.globalAlpha = ((TRAIL - st) / TRAIL) * 0.5;
            ctx.fillStyle = p.color; ctx.shadowColor = p.color; ctx.shadowBlur = 8;
            ctx.beginPath();
            ctx.arc(tx, ty, p.size * (0.4 + (TRAIL - st) / TRAIL * 0.6), 0, Math.PI * 2);
            ctx.fill(); ctx.restore();
          }
          ctx.save();
          ctx.globalAlpha = 0.95;
          ctx.fillStyle = p.color; ctx.shadowColor = p.color; ctx.shadowBlur = 16;
          ctx.beginPath(); ctx.arc(x, y, p.size, 0, Math.PI * 2); ctx.fill();
          ctx.restore();
        });
      }

      // ── NODES ──────────────────────────────────────────────────────
      NODES.forEach((n, idx) => {
        const isHov = s.hoveredNode === idx;

        if (n.main) {
          // glow halo
          const gg = ctx.createRadialGradient(n.cx, n.cy, n.r * 0.4, n.cx, n.cy, n.r * (isHov ? 2.8 : 2.2));
          gg.addColorStop(0, `rgba(59,130,246,${isHov ? 0.4 : 0.25})`);
          gg.addColorStop(1, 'rgba(59,130,246,0)');
          ctx.fillStyle = gg;
          ctx.beginPath(); ctx.arc(n.cx, n.cy, n.r * (isHov ? 2.8 : 2.2), 0, Math.PI * 2); ctx.fill();

          // hex body
          ctx.save();
          ctx.shadowColor = isHov ? '#fff' : BLU2;
          ctx.shadowBlur  = isHov ? 50 : 30;
          ctx.beginPath();
          for (let k = 0; k < 6; k++) {
            const a  = s.rot1 * 0.3 + k * Math.PI / 3;
            const px = n.cx + n.r * Math.cos(a);
            const py = n.cy + n.r * Math.sin(a);
            k === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
          }
          ctx.closePath();
          const hg = ctx.createLinearGradient(n.cx - n.r, n.cy - n.r, n.cx + n.r, n.cy + n.r);
          hg.addColorStop(0, BLU2); hg.addColorStop(1, IND);
          ctx.fillStyle   = hg; ctx.fill();


// Then replace the fillStyle line with:


          ctx.strokeStyle = isHov ? 'rgba(255,255,255,0.8)' : 'rgba(96,165,250,0.6)';
          ctx.lineWidth   = isHov ? 2 : 1.5; ctx.stroke();
          ctx.restore();

          // label
          ctx.save();
          ctx.fillStyle = WHT; ctx.font = "bold 9px 'JetBrains Mono','Courier New',monospace";
          ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          ctx.shadowColor = BLU2; ctx.shadowBlur = 8;
          n.label.split('\n').forEach((line, li) => {
            ctx.fillText(line, n.cx, n.cy - 4 + li * 11);
          });
          ctx.restore();

        } else {
          const pulse = n.pulse;
          const nr    = n.r + (isHov ? 6 : pulse * 3);
          const glowStr = isHov ? 0.55 : (0.22 + pulse * 0.12);

          // halo
          const rg2 = ctx.createRadialGradient(n.cx, n.cy, 0, n.cx, n.cy, nr * (isHov ? 3.5 : 2.5));
          rg2.addColorStop(0, hexA(n.color, glowStr));
          rg2.addColorStop(1, hexA(n.color, 0));
          ctx.fillStyle = rg2;
          ctx.beginPath(); ctx.arc(n.cx, n.cy, nr * (isHov ? 3.5 : 2.5), 0, Math.PI * 2); ctx.fill();

          // body
          ctx.save();
          ctx.shadowColor = n.color; ctx.shadowBlur = isHov ? 35 : 18 + pulse * 10;
          ctx.fillStyle   = hexA(n.color, isHov ? 0.35 : 0.18 + pulse * 0.1);
          ctx.beginPath(); ctx.arc(n.cx, n.cy, nr, 0, Math.PI * 2); ctx.fill();
          ctx.strokeStyle = n.color; ctx.lineWidth = isHov ? 2.5 : 1.5;
          ctx.beginPath(); ctx.arc(n.cx, n.cy, nr, 0, Math.PI * 2); ctx.stroke();
          ctx.restore();

          // dot
          ctx.save();
          ctx.fillStyle = n.color; ctx.shadowColor = n.color; ctx.shadowBlur = 12;
          ctx.beginPath(); ctx.arc(n.cx, n.cy, isHov ? 5.5 : 4, 0, Math.PI * 2); ctx.fill();
          ctx.restore();

          // label
          const offY = n.cy > CY ? nr + 14 : -(nr + 6);
          ctx.save();
          ctx.fillStyle    = isHov ? n.color : WHT;
          ctx.font         = `${isHov ? 'bold' : 'bold'} ${isHov ? 10 : 9}px 'JetBrains Mono','Courier New',monospace`;
          ctx.textAlign    = 'center'; ctx.textBaseline = 'middle';
          ctx.shadowColor  = n.color; ctx.shadowBlur = isHov ? 12 : 6;
          ctx.fillText(n.label, n.cx, n.cy + offY);

          // small "→ click" hint when hovered
          if (isHov) {
            ctx.font = "9px 'JetBrains Mono','Courier New',monospace";
            ctx.fillStyle = hexA(n.color, 0.7);
            ctx.fillText('click to open →', n.cx, n.cy + offY + 14);
          }
          ctx.restore();
        }
      });

      // branding
      ctx.save();
      ctx.fillStyle = 'rgba(96,165,250,0.3)';
      ctx.font = "bold 10px 'JetBrains Mono','Courier New',monospace";
      ctx.textAlign = 'right'; ctx.textBaseline = 'bottom';
      ctx.fillText('arhatinfo.com', W - 12, H - 10);
      ctx.restore();
    }

    // ── LOOP ─────────────────────────────────────────────────────────
    let raf;
    function loop() {
      update();
      draw();
      raf = requestAnimationFrame(loop);
    }
    loop();

    return () => {
      cancelAnimationFrame(raf);
      canvas.removeEventListener('mousemove',  onMouseMove);
      canvas.removeEventListener('mouseleave', onMouseLeave);
      canvas.removeEventListener('click',      onClick);
    };
  }, []);

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%', maxWidth: 460, margin: '0 auto' }}>
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: 'auto',
          display: 'block',
          background: 'transparent',
        }}
      />

      {/* ── TOOLTIP POPUP ── */}
      {tooltip && (
        <div
          style={{
            position:     'fixed',
            left:         tooltip.x,
            top:          tooltip.y - 90,
            transform:    'translateX(-50%)',
            zIndex:       1000,
            pointerEvents:'none',
            animation:    'tcFadeIn .18s ease',
          }}
        >
          <style>{`
            @keyframes tcFadeIn {
              from { opacity: 0; transform: translateX(-50%) translateY(6px); }
              to   { opacity: 1; transform: translateX(-50%) translateY(0); }
            }
          `}</style>
          <div style={{
            background:   'rgba(17, 21, 32, 0.97)',
            border:       `1px solid ${tooltip.color}`,
            borderRadius: 10,
            padding:      '10px 14px',
            minWidth:     160,
            boxShadow:    `0 0 24px ${tooltip.color}55, 0 8px 32px rgba(0,0,0,0.6)`,
            backdropFilter: 'blur(12px)',
          }}>
            {/* label */}
            <div style={{
              fontFamily:   "'JetBrains Mono','Courier New',monospace",
              fontSize:     12,
              fontWeight:   700,
              color:        tooltip.color,
              marginBottom: 4,
              letterSpacing: '.04em',
            }}>
              {tooltip.label}
            </div>
            {/* description */}
            <div style={{
              fontFamily:   'system-ui, sans-serif',
              fontSize:     11,
              color:        'rgba(200,210,230,0.8)',
              marginBottom: 8,
              lineHeight:   1.5,
            }}>
              {tooltip.desc}
            </div>
            {/* URL */}
            <div style={{
              fontFamily:   "'JetBrains Mono','Courier New',monospace",
              fontSize:     10,
              color:        tooltip.color,
              opacity:      0.7,
              display:      'flex',
              alignItems:   'center',
              gap:          4,
            }}>
              <span>↗</span>
              <span>{tooltip.url.replace('https://', '')}</span>
            </div>
            {/* arrow tip */}
            <div style={{
              position:    'absolute',
              bottom:      -6,
              left:        '50%',
              transform:   'translateX(-50%)',
              width:       10,
              height:      10,
              background:  'rgba(17,21,32,0.97)',
              border:      `1px solid ${tooltip.color}`,
              borderTop:   'none',
              borderLeft:  'none',
              transform:   'translateX(-50%) rotate(45deg)',
            }} />
          </div>
        </div>
      )}
    </div>
  );
}